import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setEmail('');
      setStatus('success');
      setStatusMessage('Vielen Dank für deine Anmeldung!');
      return;
    }

    const submitAddr = 'https://readdy.ai/api/form/d9b5de2hsavvukudolh0';

    const submitFormData = new FormData(form);
    submitFormData.delete('website_alt');
    const params = new URLSearchParams();
    submitFormData.forEach((value, key) => {
      params.append(key, value as string);
    });

    fetch(submitAddr, {
      method: 'POST',
      body: params.toString(),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then(async (res) => {
        const responseText = await res.text();
        let parsed: Record<string, unknown> | null = null;
        try {
          parsed = JSON.parse(responseText);
        } catch {
          // ignore
        }
        const serverMsg =
          (parsed as Record<string, unknown> | null)?.meta?.message as string | undefined ||
          (parsed as Record<string, unknown> | null)?.message as string | undefined ||
          responseText;

        if (res.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
          setStatus('success');
          setStatusMessage('Vielen Dank für deine Anmeldung!');
          setEmail('');
        } else if (serverMsg && String(serverMsg).includes('spam')) {
          setStatus('error');
          setStatusMessage('Ein Fehler ist aufgetreten. Bitte versuche es später erneut.');
        } else {
          setStatus('error');
          setStatusMessage(String(serverMsg || 'Ein Fehler ist aufgetreten.'));
        }
      })
      .catch(() => {
        setStatus('error');
        setStatusMessage('Ein Fehler ist aufgetreten. Bitte versuche es später erneut.');
      });
  };

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="w-full px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="inline-block cursor-pointer">
              <span className="font-heading text-xl font-semibold tracking-tight">
                OOPBuy <span className="text-primary-400">Spreadsheet</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-foreground-400 leading-relaxed max-w-xs">
              {t('footer_desc')}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">
              {t('nav_categories')}
            </h4>
            <div className="flex flex-col gap-2">
              {categories.slice(0, 7).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">&nbsp;</h4>
            <div className="flex flex-col gap-2">
              {categories.slice(7).map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-400 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-background-50 mb-4">
              {t('footer_newsletter')}
            </h4>
            <p className="text-sm text-foreground-400 mb-4 leading-relaxed">
              {t('footer_newsletter_desc')}
            </p>
            <form onSubmit={handleSubmit} data-readdy-form="">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="visually-hidden-input"
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer_email_placeholder')}
                  required
                  className="flex-1 px-4 py-2.5 text-sm bg-foreground-900 border border-foreground-700 rounded-lg text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500 transition-colors"
                />
                <button
                  type="submit"
                  className="px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  {t('footer_subscribe')}
                </button>
              </div>
              {status !== 'idle' && (
                <p className={`mt-3 text-xs ${status === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
                  {statusMessage}
                </p>
              )}
            </form>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-foreground-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-500">
            {t('footer_copyright')}
          </p>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-xs text-foreground-500 hover:text-background-50 transition-colors cursor-pointer">
              {t('nav_home')}
            </Link>
            <Link to="/oopbuy-spreadsheet-guide" className="text-xs text-foreground-500 hover:text-background-50 transition-colors cursor-pointer">
              {t('nav_guide')}
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-xs text-foreground-500 hover:text-background-50 transition-colors cursor-pointer"
            >
              Kaspreadsheet
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}